import type { GoogleProperties, Platform } from '@regia/shared';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { http } from '../../lib/api';
import { Field, Select } from '../../ui';
import { useGoogleStatus, useMetaStatus } from '../settings/Integrations';

interface SourceDraft {
  platform: Platform;
  handle: string;
  url: string;
  externalId: string;
  config: Record<string, string>;
}

function NotConnected({ what }: { what: string }) {
  return (
    <p className="rounded-xl bg-sheet-2 px-3 py-2.5 text-sm text-ink-2">
      Per leggere i numeri in automatico collega {what} nelle{' '}
      <Link to="/impostazioni" className="font-semibold text-action hover:underline">
        Impostazioni
      </Link>
      .
    </p>
  );
}

/** Sceglie da dove Regia legge i numeri del canale: pagina Facebook, account Instagram, scheda Google, proprietà del sito. */
export function ChannelSource({ draft, onChange }: { draft: SourceDraft; onChange: (patch: Partial<SourceDraft>) => void }) {
  const isMeta = draft.platform === 'facebook' || draft.platform === 'instagram';
  const isGoogle = draft.platform === 'google_business' || draft.platform === 'website';
  const meta = useMetaStatus();
  const googleStatus = useGoogleStatus();
  const google = useQuery({
    queryKey: ['google-properties'],
    queryFn: () => http.get<GoogleProperties>('/api/google/properties'),
    enabled: isGoogle && Boolean(googleStatus.data?.connected),
    staleTime: 10 * 60_000,
  });

  if (isMeta) {
    const pages = meta.data?.pages ?? [];
    if (!meta.isLoading && pages.length === 0) return <NotConnected what="Facebook" />;
    if (draft.platform === 'facebook') {
      return (
        <Field label="Pagina Facebook" hint="Da qui Regia legge follower, visualizzazioni e post">
          {(id, d) => (
            <Select
              id={id}
              aria-describedby={d}
              value={draft.externalId}
              onChange={(e) => {
                const page = pages.find((p) => p.id === e.target.value);
                onChange({ externalId: e.target.value, ...(page && !draft.handle ? { handle: page.name } : {}) });
              }}
            >
              <option value="">Non collegata</option>
              {pages.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    }
    const withIg = pages.filter((p) => p.instagram);
    return (
      <Field label="Account Instagram" hint={withIg.length ? 'Deve essere un account professionale collegato a una pagina Facebook' : 'Nessuna pagina collegata ha un Instagram professionale'}>
        {(id, d) => (
          <Select
            id={id}
            aria-describedby={d}
            value={draft.externalId}
            onChange={(e) => {
              const page = withIg.find((p) => p.instagram?.id === e.target.value);
              onChange({ externalId: e.target.value, ...(page && !draft.handle ? { handle: `@${page.instagram?.username}` } : {}) });
            }}
          >
            <option value="">Non collegato</option>
            {withIg.map((p) => (
              <option key={p.instagram?.id} value={p.instagram?.id}>
                @{p.instagram?.username} ({p.name})
              </option>
            ))}
          </Select>
        )}
      </Field>
    );
  }

  if (isGoogle) {
    if (!googleStatus.isLoading && !googleStatus.data?.connected) return <NotConnected what="Google" />;
    const props = google.data;
    if (draft.platform === 'google_business') {
      return (
        <Field label="Scheda su Google" hint={google.isLoading ? 'Carico le schede…' : 'Visualizzazioni, chiamate, indicazioni e recensioni'}>
          {(id, d) => (
            <Select
              id={id}
              aria-describedby={d}
              value={draft.externalId}
              onChange={(e) => {
                const loc = props?.business.find((b) => b.location === e.target.value);
                onChange({
                  externalId: e.target.value,
                  config: { ...draft.config, ...(loc ? { account: loc.account } : {}) },
                  ...(loc && !draft.handle ? { handle: loc.title } : {}),
                });
              }}
            >
              <option value="">Non collegata</option>
              {(props?.business ?? []).map((b) => (
                <option key={b.location} value={b.location}>
                  {b.title}
                  {b.address ? `, ${b.address}` : ''}
                </option>
              ))}
            </Select>
          )}
        </Field>
      );
    }
    return (
      <div className="flex flex-col gap-4">
        <Field label="Search Console" optional hint="Clic e ricerche da Google">
          {(id, d) => (
            <Select id={id} aria-describedby={d} value={draft.config.gsc ?? ''} onChange={(e) => onChange({ config: { ...draft.config, gsc: e.target.value } })}>
              <option value="">Nessuna</option>
              {(props?.searchConsole ?? []).map((s) => (
                <option key={s.siteUrl} value={s.siteUrl}>
                  {s.siteUrl.replace(/^sc-domain:/, '')}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Google Analytics" optional hint="Visitatori e pagine viste">
          {(id, d) => (
            <Select id={id} aria-describedby={d} value={draft.config.ga4 ?? ''} onChange={(e) => onChange({ config: { ...draft.config, ga4: e.target.value } })}>
              <option value="">Nessuna</option>
              {(props?.analytics ?? []).map((a) => (
                <option key={a.property} value={a.property}>
                  {a.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
    );
  }
  return null;
}
